import { useEffect, useState } from 'react';
import { formatDuration, type GameEntryView, type GameMatchView } from '@srcbattle/shared';
import { Avatar, Button } from './ui.tsx';

function EntryCard({
  entry,
  slot,
  match,
  selected,
  revealed,
  isWinner,
  votes,
  disabled,
  onVote,
}: {
  entry: GameEntryView;
  slot: 'a' | 'b';
  match: GameMatchView;
  selected: boolean;
  revealed: boolean;
  isWinner: boolean;
  votes: number | null;
  disabled: boolean;
  onVote: () => void;
}) {
  const played = slot === 'a' ? match.playedA : match.playedB;

  return (
    <button
      type="button"
      onClick={onVote}
      disabled={disabled}
      aria-pressed={selected}
      className={`vote-card vote-card-${slot} flex flex-col gap-2 rounded-xl border p-3 text-left transition-colors disabled:cursor-default lg:p-5 ${
        isWinner
          ? 'border-success bg-success/10'
          : selected
            ? 'border-accent bg-accent/10'
            : 'border-border hover:border-border-strong disabled:hover:border-border'
      }`}
    >
      <div className="flex items-start gap-3">
        {entry.thumbnailUrl ? (
          <img src={entry.thumbnailUrl} alt="" className="h-12 w-20 shrink-0 rounded object-cover lg:h-20 lg:w-32" />
        ) : (
          <span className="h-12 w-20 shrink-0 rounded bg-surface-raised lg:h-20 lg:w-32" />
        )}
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="rounded bg-surface-raised px-1.5 py-0.5 text-xs font-medium">
              {slot.toUpperCase()}
            </span>
            {isWinner && (
              <span className="rounded bg-success/20 px-1.5 py-0.5 text-xs text-success">
                winner
              </span>
            )}
            {!played && <span className="text-xs text-text-faint">not played yet</span>}
          </span>
          <span className="mt-1 block truncate text-sm font-medium lg:text-base">{entry.title}</span>
          <span className="block truncate text-xs text-text-faint">
            {formatDuration(entry.clipEndMs - entry.clipStartMs)} clip
          </span>
        </span>
        {votes !== null && (
          <span className="shrink-0 text-2xl font-semibold tabular-nums">{votes}</span>
        )}
      </div>

      {/* The payoff. Hidden until the match is decided, which is the whole game. */}
      {revealed && entry.submitter && (
        <div className="flex items-center gap-2 border-t border-border pt-2">
          <Avatar value={entry.submitter.avatarValue} size="sm" />
          <span className="text-sm">
            <span className="text-text-faint">submitted by </span>
            <span className="font-medium">{entry.submitter.displayName}</span>
          </span>
        </div>
      )}

      {revealed && entry.greatMindsWith && entry.greatMindsWith.length > 1 && (
        <div className="rounded-lg bg-accent/15 px-2.5 py-1.5 text-xs text-accent">
          <strong>GREAT MINDS</strong> — {entry.greatMindsWith.join(' and ')} both picked this one.
        </div>
      )}
    </button>
  );
}

export function VotePanel({
  match,
  busy,
  onVote,
  onCloseVoting,
  onDecide,
  onAdvance,
  onVoteToAdvance,
  coinTossPending = false,
  onCoinToss,
  onGuess,
}: {
  match: GameMatchView;
  busy: boolean;
  onVote: (entryId: string) => void;
  onCloseVoting: () => void;
  onDecide: (entryId: string) => void;
  onAdvance: () => void;
  onVoteToAdvance: () => void;
  coinTossPending?: boolean;
  onCoinToss: () => void;
  onGuess: (entryId: string, userId: string) => void;
}) {
  // From the server, not derived from the phase: with votingOpens 'anytime' the
  // ballot is live while a clip is still playing.
  const votingOpen = match.votingOpen;
  const revealed = match.phase === 'revealing' || match.phase === 'complete';
  const awaitingOperatorDecision =
    match.phase === 'tiebreak' && match.tally === null && match.votedCount > 0;
  const [pendingVote, setPendingVote] = useState<string | null>(null);

  // A click only selects locally. The server does not receive anything until
  // the member confirms, which prevents an accidental tap from becoming a
  // permanent vote. Changing match or tiebreak round discards stale selection.
  useEffect(() => setPendingVote(null), [match.id, match.votingRound]);
  useEffect(() => {
    if (match.myVote) setPendingVote(null);
  }, [match.myVote]);

  if (!match.a || !match.b) return null;

  const selectedVote = match.myVote ?? pendingVote;
  const voteLocked = match.myVote !== null;

  return (
    <div className="flex flex-col gap-3">
      {match.votingRound > 1 && (
        <div className="rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-sm">
          It was a tie. Vote again.
        </div>
      )}

      {match.kind === 'mirror' && (
        <div className="rounded-lg border border-accent/40 bg-accent/10 px-3 py-2 text-sm">
          <strong>Mirror match.</strong> Same song on both sides, so it goes to a coin toss.
        </div>
      )}

      <div className="vote-grid grid gap-3 sm:grid-cols-2">
        <EntryCard
          entry={match.a}
          slot="a"
          match={match}
          selected={selectedVote === match.a.id}
          revealed={revealed}
          isWinner={match.winnerEntryId === match.a.id}
          votes={match.tally?.a ?? null}
          disabled={!match.canVote || busy || voteLocked}
          onVote={() => setPendingVote(match.a?.id as string)}
        />
        <EntryCard
          entry={match.b}
          slot="b"
          match={match}
          selected={selectedVote === match.b.id}
          revealed={revealed}
          isWinner={match.winnerEntryId === match.b.id}
          votes={match.tally?.b ?? null}
          disabled={!match.canVote || busy || voteLocked}
          onVote={() => setPendingVote(match.b?.id as string)}
        />
      </div>

      {/* Round one only. The server decides that; this just draws it. After a
          match is revealed the room has been told who submitted both of these,
          so a guess box in round two is a memory test rather than a guess. */}
      {match.guessingEnabled && !revealed && (
        <div className="rounded-xl border border-border p-3">
          <p className="mb-2 text-xs text-text-faint">
            Guess the submitter.
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            {([['A', match.a, match.myGuesses.a], ['B', match.b, match.myGuesses.b]] as const).map(([label, entry, guess]) => (
              <label key={label} className="text-xs text-text-faint">Who submitted {label}?
                <select value={guess ?? ''} onChange={(event) => event.target.value && onGuess(entry.id, event.target.value)} className="mt-1 w-full rounded-lg border border-border bg-surface px-2 py-1.5 text-sm text-text">
                  <option value="">Make a guess…</option>
                  {match.guessCandidates.map((candidate) => <option key={candidate.userId} value={candidate.userId}>{candidate.displayName}</option>)}
                </select>
              </label>
            ))}
          </div>
        </div>
      )}

      {votingOpen && pendingVote && !voteLocked && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-accent/40 bg-accent/10 px-3 py-2">
          <p className="text-sm text-text-muted">Your selection is not submitted yet.</p>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setPendingVote(null)} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={() => onVote(pendingVote)} disabled={busy}>
              Confirm vote
            </Button>
          </div>
        </div>
      )}

      {votingOpen && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-surface-raised px-3 py-2">
          <div className="min-w-0 text-sm">
            <span className="font-medium">
              {voteLocked ? 'Vote locked in' : `${match.votedCount} of ${match.eligibleCount} voted`}
            </span>
            {voteLocked && (
              <span className="ml-2 text-xs text-text-faint">
                {match.votedCount} of {match.eligibleCount} voted
              </span>
            )}
            {/* Naming who is holding things up works better than a countdown,
                and nobody gets cut off for taking a moment to decide. */}
            {match.pendingVoters.length > 0 && (
              <span className="block truncate text-xs text-text-faint">
                waiting on {match.pendingVoters.join(', ')}
              </span>
            )}
          </div>

          {match.canOperate && match.votedCount < match.eligibleCount && (
            <Button variant="secondary" onClick={onCloseVoting} disabled={busy}>
              Move on
            </Button>
          )}
        </div>
      )}

      {votingOpen && match.canOperate && match.votedCount >= match.eligibleCount && (
        <Button variant="ghost" onClick={onCloseVoting} disabled={busy} className="self-start">
          Close voting now
        </Button>
      )}

      {awaitingOperatorDecision && match.canOperate && (
        <div className="rounded-lg border border-warning/40 bg-warning/10 p-3">
          <p className="mb-2 text-sm font-medium">Tied — you decide.</p>
          <div className="flex gap-2">
            <Button onClick={() => onDecide(match.a?.id as string)} disabled={busy}>
              A wins
            </Button>
            <Button onClick={() => onDecide(match.b?.id as string)} disabled={busy}>
              B wins
            </Button>
          </div>
        </div>
      )}
      {coinTossPending && match.canOperate && (
        <Button onClick={onCoinToss} disabled={busy}>Toss the coin</Button>
      )}
      {coinTossPending && !match.canOperate && <p className="text-center text-sm text-text-faint">Waiting for the host to toss the coin…</p>}

      {revealed && match.canOperate && (
        <Button onClick={onAdvance} loading={busy}>
          Next match
        </Button>
      )}

      {revealed && !match.canOperate && (
        <div className="flex flex-col items-center gap-2">
          <Button variant={match.advanceVote.mine ? 'primary' : 'secondary'} onClick={onVoteToAdvance} disabled={busy || match.advanceVote.mine}>
            {match.advanceVote.mine ? 'Ready for next round' : 'Vote for next round'} ({match.advanceVote.count}/{match.advanceVote.needed})
          </Button>
        </div>
      )}
    </div>
  );
}
